import { ScopeContext, useScope } from 'dreamstate';
import {
  createElement,
  FunctionComponent,
  ReactElement,
  ReactNode,
  useEffect,
  useState,
} from 'react';

import { Optional } from '@vimeo/core/types/generic';
import { Logger } from '@vimeo/logging';

import { ELiveSignal, TGlobalErrorSignalEvent } from '../../../store/signals';
import { ELiveErrorCategory, trackLiveError } from '../../../utils/tracking';

interface IGlobalStoreErrorProps {
  fallback: FunctionComponent<{ error: Error }>;
  children: ReactNode;
}

/**
 * Default react error boundary exists for react issues rendering errors and exceptions in components.
 * Handling of async errors/issues outside of react tree should be done manually.
 *
 * Following component listens GLOBAL_ERROR signals, unload tree and renders fallback component.
 * Make sure that it is placed above providers component in tree to unload it in case of error.
 */
export function ScopeErrorBoundary({ fallback, children }: IGlobalStoreErrorProps) {
  const [error, setError] = useState<Optional<Error>>(null);
  const scope: ScopeContext = useScope();

  useEffect(() => {
    const log: Logger = new Logger('ERROR');

    return scope.subscribeToSignals((signalEvent: TGlobalErrorSignalEvent) => {
      if (signalEvent.type === ELiveSignal.GLOBAL_ERROR) {
        log.error('Global error received:', signalEvent.data);
        trackLiveError(signalEvent.data, {
          component: 'ScopeErrorBoundary',
          category: ELiveErrorCategory.GENERIC,
          data: signalEvent.data,
        });
        setError(signalEvent.data);
      }
    });
  }, [scope]);

  return error ? createElement(fallback, { error }) : (children as ReactElement);
}
